// /clients - partner_owner sees their attributed sales list.
// Phase 6.1 Chunk f.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PortalLayout } from '../components/PortalLayout';
import {
  Badge,
  statusToBadgeVariant,
  Input,
  EmptyState,
  TableWrapper,
  SkeletonRow,
  tableStyle,
  theadStyle,
  thStyle,
  trStyle,
  tdStyle,
  colors,
  headingStyle,
  pageWrapperStyle,
  mutedStyle,
  linkStyle,
  errorBannerStyle,
} from '../components/ui';
import { useLang, fmt } from '../i18n';

interface ClientRow {
  id: string;
  tenant_id: string;
  closed_at: string;
  initial_tier: string;
  current_mrr_cad: number | null;
  status: string;
  customer_csat_score: number | null;
  customer_consent_proof_url: string | null;
  consent_status?: 'active' | 'missing' | 'staff_bypass' | 'unknown';
}

export default function Clients() {
  const { t } = useLang();
  const [rows, setRows] = useState<ClientRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const url = query ? `/api/partner/clients?q=${encodeURIComponent(query)}` : '/api/partner/clients';
        const res = await fetch(url);
        if (!res.ok) {
          if (!cancelled) setError(`HTTP ${res.status}`);
          if (!cancelled) setLoading(false);
          return;
        }
        const json = (await res.json()) as { rows: ClientRow[] };
        if (!cancelled) {
          setRows(json.rows);
          setLoading(false);
        }
      } catch {
        if (!cancelled) {
          setError('network_error');
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [query]);

  return (
    <PortalLayout>
      <div style={{ ...pageWrapperStyle, maxWidth: '1100px' }}>
        <h1 style={headingStyle}>{t.clients.title}</h1>
        <p style={{ ...mutedStyle, marginBottom: '24px' }}>
          {t.clients.subtitle}
        </p>

        <Input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t.clients.searchPlaceholder}
          inputStyle={{ maxWidth: '400px', marginBottom: '24px' }}
        />

        {loading && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <SkeletonRow columns={7} />
            <SkeletonRow columns={7} />
            <SkeletonRow columns={7} />
            <SkeletonRow columns={7} />
          </div>
        )}
        {error && (
          <div style={errorBannerStyle}>
            {fmt(t.clients.errorMsg, { error })}
          </div>
        )}
        {!loading && !error && rows.length === 0 && (
          <EmptyState
            title={t.clients.emptyTitle}
            description={t.clients.emptyDesc}
          />
        )}

        {!loading && !error && rows.length > 0 && (
          <TableWrapper>
            <table style={tableStyle}>
              <thead>
                <tr style={theadStyle}>
                  <th style={thStyle}>{t.clients.colClosed}</th>
                  <th style={thStyle}>{t.clients.colTenant}</th>
                  <th style={thStyle}>{t.clients.colTier}</th>
                  <th style={thStyle}>{t.clients.colMrr}</th>
                  <th style={thStyle}>{t.clients.colStatus}</th>
                  <th style={thStyle}>{t.clients.colCsat}</th>
                  <th style={thStyle}>{t.clients.colLoi25}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} style={trStyle}>
                    <td style={tdStyle}>{new Date(r.closed_at).toLocaleDateString()}</td>
                    <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      <Link
                        to={`/clients/${encodeURIComponent(r.tenant_id)}`}
                        style={linkStyle}
                      >
                        {r.tenant_id.substring(0, 12)}...
                      </Link>
                    </td>
                    <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {r.initial_tier}
                    </td>
                    <td style={tdStyle}>
                      {r.current_mrr_cad != null ? `$${Number(r.current_mrr_cad).toFixed(2)}` : "-"}
                    </td>
                    <td style={tdStyle}>
                      <Badge variant={statusToBadgeVariant(r.status)}>
                        {r.status}
                      </Badge>
                    </td>
                    <td style={tdStyle}>
                      {r.customer_csat_score != null ? `${r.customer_csat_score}/5` : "-"}
                    </td>
                    <td style={tdStyle}>
                      {r.consent_status === 'active' ? (
                        <span style={{ color: colors.success, fontSize: '0.75rem' }}>{t.clients.statusActive}</span>
                      ) : r.consent_status === 'staff_bypass' ? (
                        <span style={{ color: colors.muted, fontSize: '0.75rem' }}>{t.clients.statusStaff}</span>
                      ) : r.consent_status === 'missing' ? (
                        <span style={{ color: colors.danger, fontSize: '0.75rem' }}>{t.clients.loi25Missing}</span>
                      ) : r.customer_consent_proof_url ? (
                        <span style={{ color: colors.success }}>{t.clients.loi25OnFile}</span>
                      ) : (
                        <span style={{ color: colors.danger }}>{t.clients.loi25Missing}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrapper>
        )}
      </div>
    </PortalLayout>
  );
}
